import { Component, ChangeDetectionStrategy, input, output, inject, signal, effect } from '@angular/core';
import { CommonModule } from '@angular/common';
import { TagModule } from 'primeng/tag';
import { ButtonModule } from 'primeng/button';
import { CustomerDTO } from '../../services/customer.service';
import {
  SaleService,
  SaleDTO,
  SaleStatus,
  SalePipelineStatus,
  SaleType
} from '../../services/sale.service';
import { CompanyTypeEnum, getCompanyTypeLabel } from '../../core/models';
import type { MitMandateDTO, MitMandateStatusValue } from '../../core/models';
import { RouterLink } from '@angular/router';
import { AuthService } from '../../services/auth.service';
import { MIT_MANDATE_STATUS_LABELS, MitService, mitMandateStatusSeverity } from '../../services/mit.service';

@Component({
  selector: 'app-customer-read',
  templateUrl: './customer-read.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  imports: [CommonModule, RouterLink, TagModule, ButtonModule]
})
export class CustomerReadComponent {
  customer = input.required<CustomerDTO>();
  openSale = output<number>();

  private saleService = inject(SaleService);
  private mitService = inject(MitService);
  private auth = inject(AuthService);

  readonly canListMandates = this.auth.hasRole('MIT_MANDATE_LIST');
  readonly canCreateMandate = this.auth.hasRole('MIT_MANDATE_WRITE');
  mandates = signal<MitMandateDTO[]>([]);
  mandatesLoading = signal(false);
  mandateSeverity = mitMandateStatusSeverity;

  readonly CompanyTypeEnum = CompanyTypeEnum;
  readonly getCompanyTypeLabel = getCompanyTypeLabel;

  sales = signal<SaleDTO[]>([]);
  salesLoading = signal(false);
  salesLoadingMore = signal(false);
  salesHasMore = signal(false);

  private salesPage = 0;
  private readonly salesPageSize = 5;

  private readonly processStatusOptions: { value: SaleStatus; label: string }[] = [
    { value: 'DRAFT', label: 'Borrador' },
    { value: 'ISSUED', label: 'Emitida' },
    { value: 'PARTIALLY_PAID', label: 'Parcialmente pagada' },
    { value: 'PAID', label: 'Pagada' },
    { value: 'VOID', label: 'Anulada' },
    { value: 'UNCOLLECTIBLE', label: 'Incobrable' }
  ];

  private readonly pipelineStatusOptions: { value: SalePipelineStatus; label: string }[] = [
    { value: 'ACCEPTED', label: 'Aceptado' },
    { value: 'CONTRACT_ATTACHMENT', label: 'Contrato/Anexo' },
    { value: 'OC', label: 'OC' },
    { value: 'INVOICED', label: 'Facturado' },
    { value: 'INVOICE_IMPORTED', label: 'Factura Ingresada' },
    { value: 'PAID', label: 'Pagado' },
    { value: 'LOST', label: 'Perdido' }
  ];

  private readonly saleTypeOptions: { value: SaleType; label: string }[] = [
    { value: 'ONE_TIME', label: 'Venta única' },
    { value: 'SUBSCRIPTION', label: 'Suscripción' }
  ];

  private readonly contactTypeOptions: { value: string; label: string }[] = [
    { value: 'PRIMARY', label: 'Principal' },
    { value: 'OFFICE', label: 'Oficina' },
    { value: 'HOME', label: 'Casa' },
    { value: 'MOBILE', label: 'Móvil' },
    { value: 'FAX', label: 'Fax' },
    { value: 'EMAIL', label: 'Email' },
    { value: 'WEBSITE', label: 'Sitio Web' },
    { value: 'SOCIAL_MEDIA', label: 'Red Social' },
    { value: 'OTHER', label: 'Otro' }
  ];

  constructor() {
    effect(() => {
      const customerId = this.customer()?.id;
      if (customerId != null) {
        this.resetAndLoadSales(customerId);
        this.loadMandates(customerId);
      } else {
        this.mandates.set([]);
        this.sales.set([]);
        this.salesHasMore.set(false);
      }
    });
  }

  mandateStatusLabel(status: MitMandateStatusValue | undefined): string {
    return status ? MIT_MANDATE_STATUS_LABELS[status] : '—';
  }

  private loadMandates(customerId: number): void {
    if (!this.canListMandates) return;
    this.mandatesLoading.set(true);
    this.mitService.list({ customerId }, { page: 0, size: 5, sort: ['createdAt,desc'] }).subscribe({
      next: (response) => {
        if (this.customer()?.id !== customerId) return;
        this.mandates.set(response.body ?? []);
        this.mandatesLoading.set(false);
      },
      error: () => {
        this.mandates.set([]);
        this.mandatesLoading.set(false);
      }
    });
  }

  getDisplayName(customer: CustomerDTO): string {
    if (!customer) return '';
    if (customer.type === CompanyTypeEnum.PERSON) {
      const fullName = `${customer.firstName || ''} ${customer.lastName || ''}`.trim();
      return fullName || customer.legalName || customer.comercialName || '';
    }
    return customer.comercialName || customer.legalName || '';
  }

  getStatusLabel(status: SaleStatus | string | undefined): string {
    if (!status) return '—';
    return this.processStatusOptions.find((o) => o.value === status)?.label ?? String(status);
  }

  getPipelineStatusLabel(s: SalePipelineStatus | string | undefined): string {
    if (!s) return '—';
    return this.pipelineStatusOptions.find((o) => o.value === s)?.label ?? String(s);
  }

  getSaleTypeLabel(k: SaleType | string | undefined): string {
    if (!k) return '—';
    return this.saleTypeOptions.find((o) => o.value === k)?.label ?? String(k);
  }

  getContactTypeLabel(type: string | undefined): string {
    if (!type) return 'Contacto';
    return this.contactTypeOptions.find((o) => o.value === type)?.label ?? type;
  }

  getContactTypeBadgeClasses(type: string | undefined): string {
    const base = 'text-[10px] px-1.5 py-0.5 rounded shrink-0';
    switch (type) {
      case 'PRIMARY':
        return `${base} bg-sky-100 text-sky-700`;
      case 'OFFICE':
        return `${base} bg-slate-100 text-slate-600`;
      case 'HOME':
        return `${base} bg-emerald-100 text-emerald-700`;
      case 'MOBILE':
        return `${base} bg-blue-100 text-blue-700`;
      case 'FAX':
        return `${base} bg-stone-100 text-stone-600`;
      case 'EMAIL':
        return `${base} bg-violet-100 text-violet-700`;
      case 'WEBSITE':
        return `${base} bg-indigo-100 text-indigo-700`;
      case 'SOCIAL_MEDIA':
        return `${base} bg-pink-100 text-pink-700`;
      default:
        return `${base} bg-slate-100 text-slate-600`;
    }
  }

  getStatusIcon(status: SaleStatus | string | undefined): string {
    if (!status) return 'pi pi-receipt';
    switch (status) {
      case 'DRAFT':
        return 'pi pi-file-edit';
      case 'ISSUED':
        return 'pi pi-send';
      case 'PARTIALLY_PAID':
        return 'pi pi-wallet';
      case 'PAID':
        return 'pi pi-check-circle';
      case 'VOID':
        return 'pi pi-ban';
      case 'UNCOLLECTIBLE':
        return 'pi pi-exclamation-triangle';
      default:
        return 'pi pi-receipt';
    }
  }

  getStatusIconClasses(status: SaleStatus | string | undefined, compact = false): string {
    const size = compact ? 'h-7 w-7' : 'h-10 w-10';
    const base = `shrink-0 ${size} rounded-full flex items-center justify-center`;
    if (!status) return `${base} bg-slate-100 text-slate-600`;
    switch (status) {
      case 'DRAFT':
        return `${base} bg-sky-100 text-sky-600`;
      case 'ISSUED':
        return `${base} bg-violet-100 text-violet-600`;
      case 'PARTIALLY_PAID':
        return `${base} bg-amber-100 text-amber-600`;
      case 'PAID':
        return `${base} bg-emerald-100 text-emerald-600`;
      case 'VOID':
        return `${base} bg-slate-200 text-slate-600`;
      case 'UNCOLLECTIBLE':
        return `${base} bg-red-100 text-red-600`;
      default:
        return `${base} bg-slate-100 text-slate-600`;
    }
  }

  loadMoreSales(): void {
    const customerId = this.customer()?.id;
    if (customerId == null || this.salesLoadingMore() || !this.salesHasMore()) return;
    this.salesPage++;
    this.fetchSales(customerId, false);
  }

  openSaleDetails(sale: SaleDTO): void {
    if (sale?.id != null) {
      this.openSale.emit(sale.id);
    }
  }

  private resetAndLoadSales(customerId: number): void {
    this.salesPage = 0;
    this.sales.set([]);
    this.salesHasMore.set(false);
    this.fetchSales(customerId, true);
  }

  private fetchSales(customerId: number, reset: boolean): void {
    if (reset) {
      this.salesLoading.set(true);
    } else {
      this.salesLoadingMore.set(true);
    }

    this.saleService
      .listSales({ customerId }, { page: this.salesPage, size: this.salesPageSize, sort: ['createdAt,desc'] })
      .subscribe({
        next: (response) => {
          const items = response.body ?? [];
          if (reset) {
            this.sales.set(items);
          } else {
            this.sales.update((prev) => [...prev, ...items]);
          }

          const page = response.page;
          const currentPage = page?.currentPage ?? this.salesPage + 1;
          const totalPages = page?.totalPages ?? 0;
          this.salesHasMore.set(currentPage < totalPages);
          this.salesLoading.set(false);
          this.salesLoadingMore.set(false);
        },
        error: () => {
          this.salesLoading.set(false);
          this.salesLoadingMore.set(false);
        }
      });
  }
}
